import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const _norm = (s) => {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

/**
 * Pantalla de admin: lista TODOS los ejercicios de rutinas (workout_plans_monthly
 * + offline_saved_plans) que hoy no encuentran video en la biblioteca.
 * Cada fila trae un buscador para seleccionar el video correcto y asignarlo:
 * guarda `video_id` (no el nombre) en todos los ejercicios con ese nombre.
 */
export default function AdminMissingVideos() {
  const [items, setItems] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // name que se está asignando
  const [filter, setFilter] = useState('');
  const [openPicker, setOpenPicker] = useState(null); // name del ejercicio con picker abierto
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [missing, lib] = await Promise.all([
        axios.get(`${API}/admin/exercises/missing-video`),
        axios.get(`${API}/videos?sort=updated_at_desc`),
      ]);
      setItems(missing.data?.items || []);
      setVideos(Array.isArray(lib.data) ? lib.data : []);
    } catch (e) {
      toast.error('No se pudo cargar la lista de ejercicios sin video');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = _norm(filter);
    if (!q) return items;
    return items.filter((i) => _norm(i.name).includes(q));
  }, [items, filter]);

  const videosFiltered = useMemo(() => {
    const q = _norm(query);
    const sorted = [...videos].sort((a, b) =>
      _norm(a.title).localeCompare(_norm(b.title))
    );
    if (!q) return sorted;
    return sorted.filter((v) => _norm(v.title).includes(q));
  }, [videos, query]);

  const runBackfill = async () => {
    try {
      const res = await axios.post(`${API}/admin/exercises/backfill-video-ids`);
      const d = res.data;
      toast.success(
        `Backfill listo · ${d.workout_plans_monthly?.exercises_updated || 0} + ${d.offline_saved_plans?.exercises_updated || 0} ejercicios con video_id`
      );
      await load();
    } catch (e) {
      toast.error('Falló el backfill');
    }
  };

  const assign = async (exerciseName, videoId) => {
    setRunning(exerciseName);
    try {
      const res = await axios.post(`${API}/admin/exercises/assign-video`, {
        exercise_name: exerciseName,
        video_id: videoId,
      });
      const d = res.data;
      toast.success(
        `Asignado "${d.video_title}" a ${
          (d.workout_plans_monthly || 0) + (d.offline_saved_plans || 0)
        } ejercicios`
      );
      setOpenPicker(null);
      setQuery('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo asignar el video');
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-missing-videos-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">🔎 Ejercicios sin video</h2>
          <p className="text-sm text-gray-400">
            Nombres que hoy no encuentran video en la biblioteca. Asigna el
            correcto y queda guardado como <code>video_id</code> permanente.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="missing-reload">
            Recargar
          </Button>
          <Button onClick={runBackfill} data-testid="missing-backfill">
            Ejecutar backfill (video_id)
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          placeholder="Filtrar lista por nombre…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-md"
          data-testid="missing-filter-input"
        />
        <span className="text-xs text-gray-400">
          {loading ? 'Cargando…' : `${filtered.length} / ${items.length} ejercicios`}
        </span>
      </div>

      <div className="rounded-lg border border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 text-left text-gray-300 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2">Ejercicio</th>
              <th className="px-3 py-2">Grupo</th>
              <th className="px-3 py-2 text-right">Usos</th>
              <th className="px-3 py-2 text-right">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filtered.map((it) => (
              <React.Fragment key={it.name}>
                <tr className="hover:bg-gray-800/40">
                  <td className="px-3 py-2 font-medium text-white">{it.name}</td>
                  <td className="px-3 py-2 text-gray-400">{it.muscle_group || '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-400">{it.occurrences}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant={openPicker === it.name ? 'default' : 'outline'}
                      onClick={() => {
                        setOpenPicker(openPicker === it.name ? null : it.name);
                        setQuery('');
                      }}
                      data-testid={`missing-pick-${it.name.replace(/\s+/g, '-')}`}
                    >
                      {openPicker === it.name ? 'Cerrar' : 'Asignar video'}
                    </Button>
                  </td>
                </tr>
                {openPicker === it.name && (
                  <tr className="bg-gray-900/50">
                    <td colSpan={4} className="px-3 py-3">
                      <Input
                        type="text"
                        placeholder="Buscar video por nombre…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="mb-2"
                        autoFocus
                        data-testid={`missing-search-${it.name.replace(/\s+/g, '-')}`}
                      />
                      <div className="max-h-72 overflow-y-auto rounded border border-gray-700 divide-y divide-gray-800">
                        {videosFiltered.length === 0 ? (
                          <p className="p-3 text-xs text-gray-500">
                            Sin resultados.
                          </p>
                        ) : (
                          videosFiltered.map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              disabled={running === it.name}
                              onClick={() => assign(it.name, v.id)}
                              className="w-full text-left px-3 py-2 text-sm text-gray-100 hover:bg-cyan-500/15 disabled:opacity-50 flex items-center gap-2"
                              data-testid={`missing-assign-${v.id}`}
                            >
                              <span className="flex-1">
                                {v.title}
                                {v.category && (
                                  <span className="text-gray-400"> ({v.category})</span>
                                )}
                              </span>
                              {v.muscle_group && (
                                <span className="text-[10px] text-gray-500">{v.muscle_group}</span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                  No hay ejercicios pendientes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
